import { useCallback, useEffect, useRef, useState } from "react";

/** ⌘K (Mac) and Ctrl+K, with no Alt: the keys that open the search dialog. */
function isSearchShortcut(event: KeyboardEvent): boolean {
  return (event.metaKey || event.ctrlKey) && !event.altKey && event.key.toLowerCase() === "k";
}

/**
 * The state of the search dialog: whether it is open, the control that had focus when it opened (so Escape can give focus back), and the
 * shortcut that opens it from anywhere on the page, also from a field. The browser's own use of the key is replaced.
 */
export function useSearchDialog() {
  const [open, setOpen] = useState(false);
  const opener = useRef<HTMLElement | null>(null);
  const openSearch = useCallback(() => {
    // The shortcut can fire again while the dialog is open: then focus sits in its input, which is not the control to give focus back to.
    if (!open) {
      opener.current =
        document.activeElement instanceof HTMLElement ? document.activeElement : null;
    }
    setOpen(true);
  }, [open]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!isSearchShortcut(event)) return;
      event.preventDefault();
      openSearch();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [openSearch]);

  return { open, setOpen, openSearch, opener };
}
